import { FileText, Folder, Monitor, Download, Image, HardDrive } from "lucide-react";
import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    navigateToFolder,
    navigateToPathSegment,
    navigateUp,
    setFilteredContent,
    setSearchQuery,
    setViewMode,
    toggleItemSelection,
} from "../../../app/stores/file-explorer-slice";
import { RootState } from "@/app/stores/store";
import type { TreeNode } from "../../TreeView/types";
import { FileItem } from "../types";

export const useFileExplorer = () => {
    const dispatch = useDispatch();
    const { viewMode, navigationState, searchQuery, selectedItems, filteredContent, currentContent, isLoading, error } = useSelector(
        (state: RootState) => state.fileExplorer
    );

    const getSidebarNodes = useCallback((): TreeNode[] => {
        return [
            {
                id: "quick-access",
                label: "Quick access",
                icon: <Folder size={16} />,
                children: [
                    {
                        id: "desktop",
                        label: "Desktop",
                        icon: <Monitor size={16} />,
                        isQuickAccess: true,
                    },
                    { id: "downloads", label: "Downloads", icon: <Download size={16} />, children: [], isQuickAccess: true },
                    { id: "documents", label: "Documents", icon: <FileText size={16} />, children: [], isQuickAccess: true },
                    { id: "pictures", label: "Pictures", icon: <Image size={16} />, children: [], isQuickAccess: true },
                ],
            },
            {
                id: "this-pc",
                label: "This PC",
                icon: <HardDrive size={16} />,
                children: [{ id: "c-drive", label: "Local Disk (C:)", icon: <HardDrive size={16} />, isQuickAccess: false }],
            },
        ];
    }, []);

    const getIcon = useCallback((item: FileItem): React.ReactNode => {
        if (item.icon) return item.icon;
        return item.type === "folder" ? <Folder size={16} /> : <FileText size={16} />;
    }, []);

    const mapToTreeNode = useCallback(
        (item: FileItem): TreeNode => ({
            id: item.id,
            label: item.name,
            icon: getIcon(item),
            children: [],
        }),
        [getIcon]
    );

    useEffect(() => {
        const newFilteredContent = searchQuery
            ? currentContent.map(mapToTreeNode).filter((item: TreeNode) => item.label.toLowerCase().includes(searchQuery.toLowerCase()))
            : currentContent.map(mapToTreeNode);

        dispatch(setFilteredContent(newFilteredContent));
    }, [searchQuery, currentContent, dispatch, mapToTreeNode]);

    const findNodeByPath = useCallback((nodes: TreeNode[], path: string[]): TreeNode | null => {
        if (path.length === 0 || nodes.length === 0) return null;

        const targetNode = nodes.find((node) => node.label === path[0]);
        if (!targetNode) return null;

        if (path.length === 1) return targetNode;

        return findNodeByPath(targetNode.children || [], path.slice(1));
    }, []);

    return {
        viewMode,
        setViewMode: (mode: "grid" | "list") => dispatch(setViewMode(mode)),
        currentPath: navigationState.path,
        searchQuery,
        setSearchQuery: (query: string) => dispatch(setSearchQuery(query)),
        filteredContent,
        navigateToFolder: (node: TreeNode) => dispatch(navigateToFolder(node)),
        navigateUp: () => dispatch(navigateUp()),
        navigateToPathSegment: (index: number) => dispatch(navigateToPathSegment(index)),
        selectedItems,
        toggleItemSelection: (itemId: string) => dispatch(toggleItemSelection(itemId)),
        currentNode: navigationState.node,
        isLoading,
        error,
        getSidebarNodes,
    };
};
